"use client";

import { alpha, Box, Stack, Typography } from "@mui/material";
import Image from "next/image";

import { radius } from "../tokens/radius";

import type { ReactNode } from "react";

type GlassmorphicCtaProps = {
  heading: string;
  message?: string;
  backgroundImageUrl: string | null;
  backgroundSizes?: string;
  action: ReactNode;
  "data-testid"?: string;
};

/**
 * CTA glassmorphique partagé : image en fond + backdrop-blur, texte blanc.
 * Utilisé sur la homepage, la page Destination et la page DMC.
 */
export function GlassmorphicCta({
  heading,
  message,
  backgroundImageUrl,
  backgroundSizes = "(max-width: 900px) 100vw, 960px",
  action,
  "data-testid": dataTestId,
}: GlassmorphicCtaProps) {
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        position: "relative",
        borderRadius: radius.lg,
        overflow: "hidden",
        boxShadow: (theme) =>
          `2px 2px 20px 0px ${alpha(theme.palette.common.black, 0.1)}`,
        bgcolor: backgroundImageUrl ? "grey.500" : "primary.dark",
      }}
    >
      {backgroundImageUrl && (
        <Image
          src={backgroundImageUrl}
          alt=""
          fill
          sizes={backgroundSizes}
          style={{ objectFit: "cover" }}
        />
      )}
      <Stack
        spacing={3}
        sx={{
          alignItems: "center",
          position: "relative",
          px: 4,
          py: 6,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          // Voile sombre par-dessus la photo : c'est lui qui rend le texte
          // blanc lisible quelle que soit l'image de fond.
          backgroundColor: (theme) => alpha(theme.palette.common.black, 0.25),
          color: "common.white",
          textAlign: "center",
        }}
      >
        <Stack
          spacing={1}
          sx={{
            alignItems: "center",
          }}
        >
          <Typography variant="h2" component="h2" sx={{ color: "inherit" }}>
            {heading}
          </Typography>
          {message && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.15px",
                color: "inherit",
              }}
            >
              {message}
            </Typography>
          )}
        </Stack>
        {action}
      </Stack>
    </Box>
  );
}
